import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const LegalDocumentViewer = ({ document, currentLanguage, onClose }) => {
  const [fontSize, setFontSize] = useState('text-sm');
  const [isBookmarked, setIsBookmarked] = useState(false);

  const fontSizes = [
    { value: 'text-xs', label: 'Small' },
    { value: 'text-sm', label: 'Medium' },
    { value: 'text-base', label: 'Large' },
    { value: 'text-lg', label: 'Extra Large' }
  ];

  const getDocumentContent = () => {
    const content = {
      impressum: {
        en: {
          title: 'Legal Notice (Impressum)',
          sections: [
            {
              title: 'Company Information',
              content: `REXFEL Investment GmbH\nRegistered Office: Neuer Wall 50, 20354 Hamburg, Germany\nCommercial Register: Hamburg HRB 123456\nManaging Director: Dr. Alexander Müller\nVAT ID: DE123456789\n\nContact Information:\nTelephone: +49 40 123456789\nEmail: legal@rexfel.com\nWebsite: www.rexfel.com`
            },
            {
              title: 'Regulatory Information',
              content: `REXFEL Investment GmbH is regulated by:\n• German Federal Financial Supervisory Authority (BaFin)\n• License Number: 12345678\n• Hamburg Chamber of Commerce Member ID: HH-987654\n\nProfessional Indemnity Insurance:\nProvider: Allianz Versicherung AG\nPolicy Number: PI-2024-REXFEL-001\nCoverage: €10,000,000`
            },
            {
              title: 'Disclaimer',
              content: `The information on this website is for general information purposes only. REXFEL Investment GmbH does not warrant the accuracy, completeness, or usefulness of this information. Any reliance you place on such information is strictly at your own risk.\n\nInvestment services are subject to market risks. Past performance does not guarantee future results. Please consult with qualified financial advisors before making investment decisions.`
            }
          ]
        },
        de: {
          title: 'Impressum',
          sections: [
            {
              title: 'Firmeninformationen',
              content: `REXFEL Investment GmbH\nSitz der Gesellschaft: Neuer Wall 50, 20354 Hamburg, Deutschland\nHandelsregister: Hamburg HRB 123456\nGeschäftsführer: Dr. Alexander Müller\nUSt-IdNr.: DE123456789\n\nKontaktinformationen:\nTelefon: +49 40 123456789\nE-Mail: legal@rexfel.com\nWebsite: www.rexfel.com`
            },
            {
              title: 'Aufsichtsbehörde',
              content: `REXFEL Investment GmbH wird reguliert durch:\n• Bundesanstalt für Finanzdienstleistungsaufsicht (BaFin)\n• Lizenznummer: 12345678\n• Handelskammer Hamburg Mitgliedsnummer: HH-987654\n\nBerufshaftpflichtversicherung:\nAnbieter: Allianz Versicherung AG\nPolicennummer: PI-2024-REXFEL-001\nDeckungssumme: €10.000.000`
            }
          ]
        }
      },
      privacy: {
        en: {
          title: 'Privacy Policy',
          sections: [
            {
              title: 'Data Controller',
              content: `REXFEL Investment GmbH is the data controller for personal data processing on this website. We are committed to protecting your privacy and ensuring transparent data handling practices in accordance with GDPR and German data protection laws.`
            },
            {
              title: 'Data We Collect',
              content: `Personal Information: Name, email address, phone number, postal address\nFinancial Information: Investment preferences, risk tolerance, financial goals\nTechnical Data: IP address, browser type, device information, cookies\nCommunication Records: Email correspondence, chat logs, meeting notes\nWebsite Usage: Page views, click patterns, session duration`
            },
            {
              title: 'Legal Basis for Processing',
              content: `• Consent (Article 6(1)(a) GDPR): For marketing communications and optional services\n• Contract Performance (Article 6(1)(b) GDPR): For investment services and client management\n• Legal Obligation (Article 6(1)(c) GDPR): For regulatory compliance and reporting\n• Legitimate Interest (Article 6(1)(f) GDPR): For website analytics and security`
            },
            {
              title: 'Your Rights',
              content: `Under GDPR, you have the right to:\n• Access your personal data\n• Rectify inaccurate data\n• Erase your data (right to be forgotten)\n• Restrict processing\n• Data portability\n• Object to processing\n• Withdraw consent\n• Lodge a complaint with supervisory authorities`
            }
          ]
        }
      },
      cookies: {
        en: {
          title: 'Cookie Policy',
          sections: [
            {
              title: 'What Are Cookies',
              content: `Cookies are small text files stored on your device when you visit our website. They help us provide you with a better browsing experience by remembering your preferences and analyzing website usage patterns.`
            },
            {
              title: 'Types of Cookies We Use',
              content: `Strictly Necessary Cookies: Essential for website functionality, security, and user authentication. These cannot be disabled.\n\nFunctional Cookies: Enable enhanced features like language preferences, chat widgets, and personalized content.\n\nAnalytics Cookies: Help us understand website usage through tools like Google Analytics to improve user experience.\n\nMarketing Cookies: Used for targeted advertising and measuring campaign effectiveness across platforms.`
            },
            {
              title: 'Cookie Management',
              content: `You can manage your cookie preferences through:\n• Our Cookie Preference Center\n• Browser settings and controls\n• Third-party opt-out tools\n• Direct contact with our privacy team\n\nNote: Disabling certain cookies may affect website functionality and user experience.`
            }
          ]
        }
      }
    };

    return content?.[document]?.[currentLanguage] || content?.[document]?.en || {
      title: 'Document Not Available',
      sections: [{ title: 'Error', content: 'This document is not available in the selected language.' }]
    };
  };

  const documentContent = getDocumentContent();

  const handlePrint = () => {
    window.print();
  };

  const handleDownload = () => {
    const element = document?.createElement('a');
    const file = new Blob([JSON.stringify(documentContent, null, 2)], { type: 'text/plain' });
    element.href = URL.createObjectURL(file);
    element.download = `${document}-${currentLanguage}.txt`;
    document?.body?.appendChild(element);
    element?.click();
    document?.body?.removeChild(element);
  };

  return (
    <div className="bg-card border border-border rounded-brand">
      {/* Header */}
      <div className="flex items-center justify-between p-6 border-b border-border">
        <div className="flex items-center space-x-3">
          <div className="p-2 bg-primary/10 rounded-brand-sm">
            <Icon name="FileText" size={20} className="text-primary" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-foreground">{documentContent?.title}</h2>
            <p className="text-sm text-muted-foreground">
              Last updated: {new Date()?.toLocaleDateString()} • Language: {currentLanguage?.toUpperCase()}
            </p>
          </div>
        </div>

        <div className="flex items-center space-x-2">
          {/* Font Size Controls */}
          <div className="flex items-center space-x-1 mr-4">
            {fontSizes?.map((size) => (
              <Button
                key={size?.value}
                variant={fontSize === size?.value ? "default" : "ghost"}
                size="sm"
                onClick={() => setFontSize(size?.value)}
                className="px-2 py-1 text-xs"
              >
                {size?.label}
              </Button>
            ))}
          </div>

          <Button
            variant="ghost"
            size="icon"
            onClick={() => setIsBookmarked(!isBookmarked)}
            className={isBookmarked ? 'text-primary' : ''}
          >
            <Icon name={isBookmarked ? "Bookmark" : "BookmarkPlus"} size={18} />
          </Button>

          <Button variant="ghost" size="icon" onClick={handlePrint}>
            <Icon name="Printer" size={18} />
          </Button>

          <Button variant="ghost" size="icon" onClick={handleDownload}>
            <Icon name="Download" size={18} />
          </Button>

          {onClose && (
            <Button variant="ghost" size="icon" onClick={onClose}>
              <Icon name="X" size={18} />
            </Button>
          )}
        </div>
      </div>
      {/* Content */}
      <div className="p-6">
        <div className={`prose prose-slate max-w-none ${fontSize}`}>
          {documentContent?.sections?.map((section, index) => (
            <div key={index} className="mb-8">
              <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center">
                <div className="w-6 h-6 bg-primary/10 rounded-full flex items-center justify-center mr-3">
                  <span className="text-xs font-bold text-primary">{index + 1}</span>
                </div>
                {section?.title}
              </h3>
              <div className="text-muted-foreground leading-relaxed whitespace-pre-line pl-9">
                {section?.content}
              </div>
            </div>
          ))}
        </div>

        {/* Footer */}
        <div className="mt-8 pt-6 border-t border-border">
          <div className="flex items-center justify-between">
            <div className="text-sm text-muted-foreground">
              <p>Document ID: {document?.toUpperCase()}-{currentLanguage?.toUpperCase()}-{new Date()?.getFullYear()}</p>
              <p>Version: 2.1 • Effective Date: January 1, 2024</p>
            </div>
            
            <div className="flex items-center space-x-4">
              <Button variant="outline" size="sm">
                <Icon name="MessageCircle" size={16} />
                <span className="ml-2">Ask Questions</span>
              </Button>
              
              <Button variant="outline" size="sm">
                <Icon name="Flag" size={16} />
                <span className="ml-2">Report Issue</span>
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LegalDocumentViewer;